import { ProductionWorkflowLineDto } from "../../../../../core/models/project-workflow/productionWorkflowLineDto";

export interface EmailTemplateParams {
  orderNumber: string;
  clientPortalLink: string;
  clientName: string;
  orderLines: ProductionWorkflowLineDto[];
  /**
   * Optional rich-text HTML typed by the user in the email composer.
   * When provided it replaces the default copy paragraphs in the email body.
   * When absent or empty the default copy is used.
   */
  customBodyHtml?: string;
}

function escapeHtml(value: string | null | undefined): string {
  return (value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return "";
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime())
    ? dateStr
    : d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
}

/** Generates a fully self-contained HTML email for the client portal proof + OC approval flow. */
export function generateClientPortalEmailHtml(
  params: EmailTemplateParams
): string {
  const {
    orderNumber,
    clientPortalLink,
    clientName,
    orderLines,
    customBodyHtml,
  } = params;

  const bodyContent = customBodyHtml?.trim()
    ? customBodyHtml
    : `<p style="margin:0 0 16px;font-size:14px;line-height:1.6;font-weight:400;color:#444444;">
            Thank you for your Order <strong>${escapeHtml(orderNumber)}</strong>.
          </p>
          <p style="margin:0 0 16px;font-size:14px;line-height:1.6;font-weight:400;color:#444444;">
            Please see Order Confirmation and Proof(s) attached in the PDF. If all is approved, please continue to the project page for approval.
          </p>
          <p style="margin:0 0 16px;font-size:14px;line-height:1.6;font-weight:400;color:#444444;">
            Project Link: <strong><a href="${escapeHtml(clientPortalLink)}" style="color:#0b0b0b;">${escapeHtml(clientPortalLink)}</a></strong>
          </p>
          <p style="margin:0 0 16px;font-size:14px;line-height:1.6;font-weight:400;color:#444444;">
            If you have any edits or concerns, please don&#39;t hesitate to reach out.
          </p>`;

  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const deliveryDate = formatDate(
    orderLines.find(l => l.inHandDate)?.inHandDate
  );
  const itemCount = orderLines.length;

  const orderLineRows = orderLines
    .map(
      line => `
              <tr>
                <td style="height:50px;padding:0 10px;border-bottom:1px solid #e8e8e8;font-size:12px;font-weight:600;color:#1a1a1a;vertical-align:middle;">${escapeHtml(line.nickName || line.jobNameD365 || "")}</td>
                <td style="height:50px;padding:0 10px;border-bottom:1px solid #e8e8e8;font-size:12px;font-weight:600;color:#1a1a1a;vertical-align:middle;width:52px;">${line.quantityD365}</td>
              </tr>`
    )
    .join("");

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Nadel Order Approval</title>
  </head>
  <body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,Helvetica,sans-serif;color:#111111;width:100%;">
    <div style="width:100%;padding:28px 0 22px;background:#f4f4f4;">
      <div style="max-width:600px;margin:0 auto;background:#ffffff;border-radius:16px;border:1px solid #e8e8e8;overflow:hidden;">

        <!-- Header: Nadel wordmark (base64 SVG data URI) -->
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="padding:28px 40px 22px;text-align:center;background:#ffffff;">
              <img src="cid:nadelWordmark" width="68" height="20" alt="Nadel" style="display:block;margin:0 auto;" />
            </td>
          </tr>
        </table>

        <!-- Hero: base64 SVG data URI -->
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="padding:0 25px;">
              <img src="cid:nadelBanner" width="550" alt="Nadel" style="display:block;width:100%;max-width:550px;border-radius:10px;" />
            </td>
          </tr>
        </table>

        <!-- Content section -->
        <div style="max-width:550px;width:100%;margin:0 auto;padding:18px 25px 0;">
          ${bodyContent}

          <!-- Divider -->
          <div style="height:1px;margin:17px 0;background:#e8e8e8;font-size:0;line-height:0;"></div>

          <!-- Order Lines header -->
          <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:17px;border-collapse:collapse;">
            <tr>
              <td style="text-align:left;"><h2 style="margin:0;font-size:13px;line-height:1;font-weight:700;color:#111111;">Order Lines</h2></td>
              <td style="text-align:right;font-size:13px;line-height:1;font-weight:400;color:#888888;">${itemCount} item${itemCount !== 1 ? "s" : ""} total</td>
            </tr>
          </table>

          <!-- Order Lines table -->
          <table width="100%" style="border-collapse:collapse;border:1px solid #e8e8e8;border-radius:8px;overflow:hidden;" cellpadding="0" cellspacing="0">
            <thead>
              <tr>
                <th style="height:32px;padding:0 10px;background:#f5f5f5;border-bottom:1px solid #e8e8e8;font-size:10px;font-weight:700;color:#888888;letter-spacing:0.7px;text-align:left;text-transform:uppercase;">Product</th>
                <th style="height:32px;padding:0 10px;background:#f5f5f5;border-bottom:1px solid #e8e8e8;font-size:10px;font-weight:700;color:#888888;letter-spacing:0.7px;text-align:left;text-transform:uppercase;width:52px;">Qty</th>
              </tr>
            </thead>
            <tbody>
              ${orderLineRows || '<tr><td colspan="2" style="padding:0 10px;height:50px;">&#8212;</td></tr>'}
            </tbody>
          </table>

          <!-- Project metadata box -->
          <table width="100%" style="margin-top:17px;background:#f5f5f5;border-radius:10px;border-collapse:collapse;" cellpadding="0" cellspacing="0">
            <tr>
              <td style="padding:16px 22px 8px;width:50%;vertical-align:top;">
                <span style="display:block;margin-bottom:7px;font-size:10px;font-weight:700;color:#888888;letter-spacing:0.8px;text-transform:uppercase;">Project</span>
                <span style="display:block;font-size:12px;font-weight:700;color:#1a1a1a;">${escapeHtml(orderNumber)}</span>
              </td>
              <td style="padding:16px 22px 8px;width:50%;vertical-align:top;">
                <span style="display:block;margin-bottom:7px;font-size:10px;font-weight:700;color:#888888;letter-spacing:0.8px;text-transform:uppercase;">Project Date</span>
                <span style="display:block;font-size:12px;font-weight:700;color:#1a1a1a;">${today}</span>
              </td>
            </tr>
            <tr>
              <td style="padding:8px 22px 17px;vertical-align:top;">
                <span style="display:block;margin-bottom:7px;font-size:10px;font-weight:700;color:#888888;letter-spacing:0.8px;text-transform:uppercase;">Customer</span>
                <span style="display:block;font-size:12px;font-weight:700;color:#1a1a1a;">${escapeHtml(clientName)}</span>
              </td>
              <td style="padding:8px 22px 17px;vertical-align:top;">
                <span style="display:block;margin-bottom:7px;font-size:10px;font-weight:700;color:#888888;letter-spacing:0.8px;text-transform:uppercase;">Delivery Date</span>
                <span style="display:block;font-size:12px;font-weight:700;color:#1a1a1a;">${deliveryDate}</span>
              </td>
            </tr>
          </table>

          <!-- Divider -->
          <div style="height:1px;margin:17px 0;background:#e8e8e8;font-size:0;line-height:0;"></div>

          <!-- Attachment note -->
          <table width="100%" style="margin:17px 0 16px;background:#f5f5f5;border-top:1px solid #1a1a1a;border-right:1px solid #1a1a1a;border-bottom:1px solid #1a1a1a;border-left:4px solid #111111;border-radius:6px;border-collapse:collapse;" cellpadding="0" cellspacing="0">
            <tr>
              <td style="width:28px;padding:11px 6px 11px 14px;font-size:16px;vertical-align:top;">&#128206;</td>
              <td style="padding:11px 14px 11px 0;font-size:12px;color:#5c5c5c;line-height:20px;">A <strong style="font-weight:700;color:#222222;">Project Summary PDF</strong> is attached to this email &mdash; open your email attachment to download it.</td>
            </tr>
          </table>
        </div>

        <!-- Footer -->
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="border-top:1px solid #e8e8e8;padding:16px 0;text-align:center;">
              <img src="cid:nadelLogo" width="35" height="35" alt="Nadel" style="display:block;margin:0 auto;" />
            </td>
          </tr>
        </table>

      </div>
    </div>
  </body>
</html>`;
}
